// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Pan/zoom for a fixed-size stage inside a scrolling viewport.
 *
 * The transform lives in a ref and is applied by mutating the stage element's
 * `style.transform` directly, so dragging never re-renders. At 300+ absolutely
 * positioned nodes, routing every pointer move through React state drops frames
 * for no benefit — nothing about the node tree depends on the viewport.
 *
 * Keeping the transform out of React state also means a data refresh cannot
 * reset the viewport: the 60-second telemetry poll re-renders the nodes, and the
 * stage keeps exactly the pan and zoom the operator left it at.
 *
 * Written against the interaction model of the approved design prototype
 * (`ui-mocks/official/Helios Bindings.dc.html`, `chainDown`/`chainMove`/
 * `chainUp`/`fitChain`/`zoomAbout`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface UseStageTransformOptions {
  /** Stage size in stage coordinates. */
  contentWidth: number;
  contentHeight: number;
  minZoom?: number;
  maxZoom?: number;
  /** Padding kept between the stage bounds and the viewport edge when fitting. */
  padding?: number;
  /**
   * Height, in px, kept clear at the top of the viewport when fitting, for
   * chrome that floats over the canvas (a toolbar). The fit frames the stage
   * in the viewport below it.
   */
  insetTop?: number;
  /** Pointer travel, in px, before a press becomes a drag rather than a click. */
  dragThresholdPx?: number;
  /** Keyboard pan distance per arrow press. */
  panStepPx?: number;
  /**
   * Changes to this value re-fit the stage, unless the user has already panned
   * or zoomed. Pass the graph's structure key so a genuinely different graph
   * gets framed, but a telemetry poll does not yank the viewport.
   */
  fitKey?: string;
}

export interface ViewportSize {
  width: number;
  height: number;
}

export interface UseStageTransformResult {
  viewportRef: React.RefObject<HTMLDivElement>;
  stageRef: React.RefObject<HTMLDivElement>;
  /** Spread onto the viewport element. */
  viewportProps: {
    onPointerDown: (_event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (_event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (_event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (_event: React.PointerEvent<HTMLDivElement>) => void;
    onKeyDown: (_event: React.KeyboardEvent<HTMLDivElement>) => void;
    tabIndex: number;
  };
  zoomIn: () => void;
  zoomOut: () => void;
  /** Frame the whole stage. Pass a size explicitly when the DOM cannot measure. */
  fit: (_viewport?: ViewportSize) => void;
  /** True when the last press travelled far enough to count as a drag. */
  didDrag: () => boolean;
  /** Current zoom, as a percentage rounded to 5%. For the a11y live region. */
  zoomPercent: number;
}

const BUTTON_ZOOM_FACTOR = 1.25;
const WHEEL_ZOOM_FACTOR = 1.12;

/**
 * Below this zoom the stage carries `is-zoomed-out`, and the stylesheet uses it
 * to drop the running flow dashes on the conductors.
 *
 * Those dashes animate `stroke-dashoffset` inside a `will-change: transform`
 * layer. Zoomed in, only a handful of conductors are in the viewport and the
 * per-frame repaint is cheap; zoomed out, the whole plant is on screen, so every
 * frame invalidates a layer several thousand stage-pixels wide and the canvas
 * flickers hard. The dash pattern is sub-pixel at that scale anyway — stopping
 * the animation costs nothing a reader could see, and the green overlay still
 * marks which runs carry load.
 */
export const LOW_ZOOM = 0.55;
export const LOW_ZOOM_CLASS = 'is-zoomed-out';

/**
 * Take pointer capture so a pan survives the pointer leaving the viewport.
 *
 * Called from `pointermove`, once the press has travelled past the drag
 * threshold — deliberately NOT from `pointerdown`. Capture retargets every
 * later event for that pointer to the capturing element, and per the Pointer
 * Events spec that includes the compatibility mouse events: `pointerup` and
 * the `click` derived from it. Capturing on press therefore made the viewport
 * the click target for every press, so a chip's own `onClick` never fired and
 * selecting an asset was impossible. Capturing only after real movement means
 * a drag still gets capture while a plain click never does.
 *
 * jsdom implements neither capture method, hence the typeof guard.
 */
const capturePointer = (target: Element, pointerId: number, state: { captured: boolean }): void => {
  if (typeof target.setPointerCapture !== 'function') return;
  try {
    target.setPointerCapture(pointerId);
    state.captured = true;
  } catch {
    // Some browsers reject capture for already-released pointers.
  }
};

interface TransformState {
  x: number;
  y: number;
  z: number;
  dragging: boolean;
  /** Whether the current (or most recent) press travelled past the threshold. */
  moved: boolean;
  /** Pointer position when the press started. */
  startX: number;
  startY: number;
  /** Transform offset when the press started. */
  baseX: number;
  baseY: number;
  pointerId: number | null;
  /** Whether this press ever took pointer capture, so release can be skipped. */
  captured: boolean;
  /** Set once the user pans or zooms, which suppresses automatic re-fitting. */
  userAdjusted: boolean;
}

export const useStageTransform = (options: UseStageTransformOptions): UseStageTransformResult => {
  const {
    contentWidth,
    contentHeight,
    minZoom = 0.15,
    maxZoom = 2.6,
    padding = 40,
    insetTop = 0,
    dragThresholdPx = 4,
    panStepPx = 40,
    fitKey,
  } = options;

  const viewportRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const transform = useRef<TransformState>({
    x: 0,
    y: 0,
    z: 1,
    dragging: false,
    moved: false,
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
    pointerId: null,
    captured: false,
    userAdjusted: false,
  });

  const [zoomPercent, setZoomPercent] = useState(100);

  const apply = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const { x, y, z } = transform.current;
    stage.style.transform = `translate(${x}px, ${y}px) scale(${z})`;
    // Mutated here rather than held in state for the same reason as the
    // transform: a wheel gesture must not re-render 300 nodes per frame.
    stage.classList.toggle(LOW_ZOOM_CLASS, z < LOW_ZOOM);
  }, []);

  /** Publish the zoom level for the live region. Called at gesture end only. */
  const commitZoom = useCallback(() => {
    const next = Math.round((transform.current.z * 100) / 5) * 5;
    setZoomPercent(prev => (prev === next ? prev : next));
  }, []);

  const measureViewport = useCallback((): ViewportSize | null => {
    const el = viewportRef.current;
    if (!el) return null;
    const width = el.clientWidth;
    const height = el.clientHeight;
    // jsdom reports 0 for both; callers pass an explicit size in tests.
    if (width <= 0 || height <= 0) return null;
    return { width, height };
  }, []);

  /** The scale at which the whole stage is framed inside `size`, with padding. */
  const fitScale = useCallback(
    (size: ViewportSize) =>
      Math.min(
        size.width / (contentWidth + padding * 2),
        (size.height - insetTop) / (contentHeight + padding * 2),
        // Never scale up past 1:1 — a small graph blown up to fill the viewport
        // looks broken, and the chip type sizes are fixed by the design.
        1
      ),
    [contentHeight, contentWidth, padding, insetTop]
  );

  /**
   * How far out the operator may zoom: the fit, not a fixed floor.
   *
   * Zooming out past the point where the whole drawing is already framed only
   * shrinks the plant into a smudge in the middle of an empty canvas — there is
   * nothing further to reveal. `minZoom` stays as the backstop for a drawing
   * too large to fit at all, and for the viewport-less case (jsdom).
   */
  const floorZoom = useCallback((): number => {
    const size = measureViewport();
    if (!size || contentWidth <= 0 || contentHeight <= 0) return minZoom;
    return Math.min(Math.max(minZoom, fitScale(size)), maxZoom);
  }, [contentHeight, contentWidth, fitScale, maxZoom, measureViewport, minZoom]);

  const clampZoom = useCallback(
    (z: number) => Math.min(maxZoom, Math.max(floorZoom(), z)),
    [floorZoom, maxZoom]
  );

  const fit = useCallback(
    (viewport?: ViewportSize) => {
      const size = viewport ?? measureViewport();
      if (!size || contentWidth <= 0 || contentHeight <= 0) return;

      const z = clampZoom(fitScale(size));

      transform.current.z = z;
      transform.current.x = (size.width - contentWidth * z) / 2;
      transform.current.y =
        insetTop + Math.max(padding / 2, (size.height - insetTop - contentHeight * z) / 2);
      transform.current.userAdjusted = false;
      apply();
      commitZoom();
    },
    [
      apply,
      clampZoom,
      commitZoom,
      contentHeight,
      contentWidth,
      fitScale,
      measureViewport,
      padding,
      insetTop,
    ]
  );

  /** Zoom while keeping the stage point under (px, py) fixed on screen. */
  const zoomAbout = useCallback(
    (px: number, py: number, factor: number) => {
      const state = transform.current;
      const z = clampZoom(state.z * factor);
      // All the way out is the fit view, centred — holding a point fixed there
      // would frame the whole drawing off in a corner, which is the thing
      // zooming out was trying to get away from.
      // `measureViewport` gates it because a fit with no measurable viewport
      // is a no-op, and swallowing the zoom-out instead would wedge the zoom.
      if (factor < 1 && z === floorZoom() && measureViewport()) {
        fit();
        return;
      }
      if (z === state.z) return;
      const ratio = z / state.z;
      state.x = px - (px - state.x) * ratio;
      state.y = py - (py - state.y) * ratio;
      state.z = z;
      state.userAdjusted = true;
      apply();
    },
    [apply, clampZoom, fit, floorZoom, measureViewport]
  );

  const zoomAboutCenter = useCallback(
    (factor: number) => {
      const size = measureViewport() ?? { width: 0, height: 0 };
      zoomAbout(size.width / 2, size.height / 2, factor);
      commitZoom();
    },
    [commitZoom, measureViewport, zoomAbout]
  );

  const zoomIn = useCallback(() => zoomAboutCenter(BUTTON_ZOOM_FACTOR), [zoomAboutCenter]);
  const zoomOut = useCallback(() => zoomAboutCenter(1 / BUTTON_ZOOM_FACTOR), [zoomAboutCenter]);

  const panBy = useCallback(
    (dx: number, dy: number) => {
      transform.current.x += dx;
      transform.current.y += dy;
      transform.current.userAdjusted = true;
      apply();
    },
    [apply]
  );

  // --- Pointer drag ------------------------------------------------------

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    // Only the primary button pans; right-click is left for context menus.
    if (event.button !== 0) return;
    const state = transform.current;
    state.dragging = true;
    // Reset on press rather than release: the click event that follows
    // pointerup still needs to see whether *this* press was a drag.
    state.moved = false;
    state.startX = event.clientX;
    state.startY = event.clientY;
    state.baseX = state.x;
    state.baseY = state.y;
    state.pointerId = event.pointerId;
    state.captured = false;
    // Deliberately NOT taking pointer capture here — see `capturePointer`.
  }, []);

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const state = transform.current;
      if (!state.dragging) return;
      // Only the pointer that started the drag may move it. A second finger on a
      // touch screen also emits pointermove, and without this it would pan the
      // stage from wherever the first press happened to start.
      if (state.pointerId !== null && event.pointerId !== state.pointerId) return;
      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;
      if (!state.moved && Math.hypot(dx, dy) > dragThresholdPx) {
        state.moved = true;
        state.userAdjusted = true;
        capturePointer(event.currentTarget, event.pointerId, state);
      }
      if (!state.moved) return;
      state.x = state.baseX + dx;
      state.y = state.baseY + dy;
      apply();
    },
    [apply, dragThresholdPx]
  );

  const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const state = transform.current;
    if (!state.dragging) return;
    // Likewise, a secondary pointer lifting must not end the drag the primary
    // one is still performing.
    if (state.pointerId !== null && event.pointerId !== state.pointerId) return;
    state.dragging = false;
    const target = event.currentTarget;
    // Only release what we actually captured: releasing an uncaptured pointer
    // throws NotFoundError, and after this change most presses never capture.
    if (state.captured && typeof target.releasePointerCapture === 'function') {
      try {
        if (state.pointerId != null) target.releasePointerCapture(state.pointerId);
      } catch {
        // Pointer already released.
      }
    }
    state.captured = false;
    state.pointerId = null;
  }, []);

  const didDrag = useCallback(() => transform.current.moved, []);

  // --- Wheel -------------------------------------------------------------

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    // React attaches `wheel` at the root as a passive listener, so
    // `preventDefault()` inside `onWheel` is a silent no-op and the page
    // scrolls instead of the stage zooming. It has to be registered directly.
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = event.deltaY < 0 ? WHEEL_ZOOM_FACTOR : 1 / WHEEL_ZOOM_FACTOR;
      zoomAbout(event.clientX - rect.left, event.clientY - rect.top, factor);
      commitZoom();
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [commitZoom, zoomAbout]);

  // --- Keyboard ----------------------------------------------------------

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? panStepPx * 4 : panStepPx;
      switch (event.key) {
        case 'ArrowLeft':
          panBy(step, 0);
          break;
        case 'ArrowRight':
          panBy(-step, 0);
          break;
        case 'ArrowUp':
          panBy(0, step);
          break;
        case 'ArrowDown':
          panBy(0, -step);
          break;
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
        case '_':
          zoomOut();
          break;
        case '0':
          fit();
          break;
        default:
          // Everything else (Tab, Escape, Enter) belongs to the nodes and the
          // page, so it must keep bubbling.
          return;
      }
      event.preventDefault();
    },
    [fit, panBy, panStepPx, zoomIn, zoomOut]
  );

  // --- Automatic fitting -------------------------------------------------

  // Frame a newly-loaded (or structurally changed) graph, but never override a
  // viewport the operator has set themselves.
  useEffect(() => {
    if (transform.current.userAdjusted) return;
    fit();
  }, [fit, fitKey]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (transform.current.userAdjusted) return;
      fit();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fit]);

  const viewportProps = useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
      tabIndex: 0,
    }),
    [endDrag, onKeyDown, onPointerDown, onPointerMove]
  );

  return { viewportRef, stageRef, viewportProps, zoomIn, zoomOut, fit, didDrag, zoomPercent };
};
